import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import {
  ADMIN_OPERATE_CAPABILITY,
  fetchAdminCapabilities,
} from '@/lib/adminCapability';

export type AdminCapabilityStatus =
  | 'idle'
  | 'loading'
  | 'allowed'
  | 'denied'
  | 'unavailable';

type CapabilityVerification = {
  key: string;
  status: AdminCapabilityStatus;
};

/**
 * Read the current session's server-issued Admin capability.
 *
 * This hook only controls presentation. Every Admin route and mutation still
 * checks the bearer subject and server-side capability before doing work.
 */
export function useAdminCapability() {
  const { user, isAuthenticated, isLoading, getAccessToken } = useAuth();
  const userId = user?.id ?? null;
  const verificationKey = `${isAuthenticated ? 'authenticated' : 'anonymous'}:${isLoading ? 'loading' : 'ready'}:${userId ?? ''}`;
  const getAccessTokenRef = useRef(getAccessToken);
  const [verification, setVerification] = useState<CapabilityVerification>({
    key: '',
    status: 'idle',
  });

  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  }, [getAccessToken]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !userId || typeof getAccessTokenRef.current !== 'function') {
      return;
    }

    let cancelled = false;

    void Promise.resolve()
      .then(() => {
        if (cancelled) return null;
        return getAccessTokenRef.current();
      })
      .then((accessToken) => {
        if (!accessToken) {
          if (!cancelled) {
            setVerification({ key: verificationKey, status: 'denied' });
          }
          return null;
        }

        return fetchAdminCapabilities(accessToken);
      })
      .then((capabilities) => {
        if (cancelled || !capabilities) return;
        setVerification({
          key: verificationKey,
          status: capabilities.includes(ADMIN_OPERATE_CAPABILITY) ? 'allowed' : 'denied',
        });
      })
      .catch(() => {
        if (!cancelled) {
          setVerification({ key: verificationKey, status: 'unavailable' });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, userId, verificationKey]);

  const status = !isAuthenticated || !userId || typeof getAccessToken !== 'function'
    ? 'denied'
    : isLoading
      ? 'loading'
      : verification.key === verificationKey
        ? verification.status
        : 'loading';

  return {
    status,
    isAllowed: status === 'allowed',
  } as const;
}
